import {
  useCallback,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import {
  CmarkdowntablePopoverOpenAction,
  resolveCmarkdowntablePopoverOpenState,
} from './CmarkdowntablePopoverOpenState';
import type {
  CmarkdowntableCell,
  CmarkdowntablePopoverEntry,
  OpenTablePopoverRowInput,
} from './Cmarkdowntable.types';

interface UseCmarkdowntablePopoverOpenRowInput {
  activeRowIndex: number | null;
  headerCells: CmarkdowntableCell[];
  activeAnchorRef: RefObject<HTMLDivElement | null>;
  closePopover: () => void;
  restoreRememberedManualRect: () => boolean;
  updatePopoverPosition: () => void;
  setActiveRowIndex: Dispatch<SetStateAction<number | null>>;
  setPopoverEntries: Dispatch<SetStateAction<CmarkdowntablePopoverEntry[] | null>>;
  setPopoverIdValue: Dispatch<SetStateAction<string>>;
}

export function useCmarkdowntablePopoverOpenRow(
  input: UseCmarkdowntablePopoverOpenRowInput,
): (input: OpenTablePopoverRowInput) => void {
  const {
    activeRowIndex,
    headerCells,
    activeAnchorRef,
    closePopover,
    restoreRememberedManualRect,
    updatePopoverPosition,
    setActiveRowIndex,
    setPopoverEntries,
    setPopoverIdValue,
  } = input;

  return useCallback((rowInput: OpenTablePopoverRowInput): void => {
    const openState = resolveCmarkdowntablePopoverOpenState({
      activeRowIndex,
      headerCells,
      row: rowInput.row,
      rowIndex: rowInput.rowIndex,
    });

    if (openState.action === CmarkdowntablePopoverOpenAction.Close) {
      closePopover();
      return;
    }

    activeAnchorRef.current = rowInput.anchorElement;
    setActiveRowIndex(rowInput.rowIndex);
    setPopoverEntries(openState.entries);
    setPopoverIdValue(openState.idValue);

    if (restoreRememberedManualRect()) {
      return;
    }

    updatePopoverPosition();
  }, [
    activeAnchorRef,
    activeRowIndex,
    closePopover,
    headerCells,
    restoreRememberedManualRect,
    setActiveRowIndex,
    setPopoverEntries,
    setPopoverIdValue,
    updatePopoverPosition,
  ]);
}
